import {useNavigate, useParams} from "react-router-dom";
import {useEffect, useState, useMemo} from "react";
import {deleteDrop, getDropById, getUser, markDropUsed} from "../../api.js";
import {BackButton} from "../../shared/BackButton.jsx";
import {formatDate} from "../../services/utils.js";
import {getDeliveryOrder, updateDeliveryOrder, uploadPhotosDeliveryOrder} from "./api.js";
import {Loading} from "../../shared/Loading.jsx";
import {fetchProducts} from "../../api.js";
import {formatOrderString} from "./DeliveryOrdersList.jsx";
import {openModalPrompt} from "./ModalPrompt.jsx";

const ORDER_STATUS = {
  "CREATED": "Создан",
  "CONFIRMED": "Подтвержден",
  "ASSIGNED": "Назначен Стореману",
  "DONE": "Выполнен",
  "RECEIVED": "Получен",
  "CANCELLED": "Отменен"
}

export const DeliveryOrder = ({}) => {

  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(false)
  const [products, setProducts] = useState({})
  const [editPhotos, setEditPhotos] = useState(false)
  const [photos, setPhotos] = useState([])
  const [editComment, setEditComment] = useState(false)
  const [commentValue, setCommentValue] = useState('')

  useEffect(() => {
    fetchProducts().then(setProducts).catch((error) => console.error(error));
  }, []);

  useEffect(() => {
    getUser().then((user) => {
      setUser(user)
    }).catch((error) => console.error(error))
  }, []);

  const navigate = useNavigate()
  const [order, setOrder] = useState({})
  const { id } = useParams();

  const fetchOrder = () => {
    setLoading(true)
    getDeliveryOrder(id).then((data) => {
      setOrder(data)
      setLoading(false)
    }).catch((error) => {
      console.error(error)
      setLoading(false)
    })
  }

  const uploadPhotos = () => {
    const formData = new FormData()

    if (photos && photos.length) {
      photos.forEach(photo => {
        if(photo){
          formData.append('photos', photo);
        }
      });
    }

    uploadPhotosDeliveryOrder(order.id, formData).then(() => {
      fetchOrder()
    })

    setEditPhotos(false)
  }

  const onPhotoAdded = async(index, event) => {
    if(index === 0){
      setPhotos([
        event.target.files[0],
        photos[1]
      ])
    }
    if(index === 1){
      setPhotos([
        photos[0],
        event.target.files[0]
      ])
    }
  }

  useEffect(() => {
    fetchOrder()
  }, [id])

  const updateOrder = (data) => {
    updateDeliveryOrder({...data, orderId: id}).then(() => {
      getDeliveryOrder(id).then((order) => {
        setOrder(order)
      })
    })
  }

  const status = useMemo(() => {
    if(!order) return null

    if(order.cancelled_at){
      return ORDER_STATUS.CANCELLED
    }
    if(order.received_at){
      return ORDER_STATUS.RECEIVED
    }
    if(order.done_at){
      return ORDER_STATUS.DONE
    }
    if(order.confirmed_at){
      return ORDER_STATUS.CONFIRMED
    }
    if(order.created_at){
      return ORDER_STATUS.CREATED
    }
  }, [order])

  const orderData = useMemo(() => {
    if(!order || !order.items) return "";
    return formatOrderString(order.items.reduce((acc, item) => {
      acc[item.productId] = item.quantity;
      return acc;
    }, {}), products)
  },[products, order])

  const orderFormatted = useMemo(() => {
    return orderData.replaceAll(", ", "\n");
  }, [orderData])

  const cancelEditPhotos = () => {
    setEditPhotos(false)
    fetchOrder()
  }

  const startEditComment = () => {
    setEditComment(true)
    setCommentValue(order.comment || '')
  }

  const cancelEditComment = () => {
    setEditComment(false)
    setCommentValue('')
  }

  const saveComment = () => {
    updateDeliveryOrder({
      orderId: id,
      comment: commentValue
    }).then(() => {
      setEditComment(false)
      fetchOrder()
    }).catch((error) => {
      console.error('Ошибка сохранения комментария:', error)
    })
  }

  const onDoneOrder = async() => {
    openModalPrompt("Введите комментарий для клиента (и не забудь фотки загрузить)", s => s).then((reason) => {
      updateOrder({
        doneAt: (new Date()).toISOString(),
        receivedAt: null,
        cancelledAt: null,
        comment: reason
      })
    }, () => {

    })
  }

  return <div className="text-center">
    <h2 className="text-2xl font-bold mb-4">Заказ под адрес</h2>

    <BackButton linkTo="/delivery-orders" />

    <h2 className="text-xl font-semibold mb-6">Заказ {id}</h2>

    {
      loading ? <Loading/> : <div className="mt-5 p-2 max-w-4xl mx-auto">
        <div className="text-lg font-bold mb-2">Статус: {status}</div>
        <div className="text-base mb-5">Оператор: {order.operator_full_name || "Не задан"}</div>
        
        <div className="flex flex-col gap-6 items-center">

          {/* Кнопка управления */}
          <div className="well">
            <p className="text-lg font-bold mb-4">Управление:</p>
            {
              order.done_at ? 
                <div className="text-green-600 font-semibold">Заказ выполнен: {formatDate(order.done_at)}</div> : 
                <button 
                  onClick={onDoneOrder}
                  className="bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-6 rounded-lg text-lg transition-colors"
                >
                  {order.done_at && <span className="mr-2">✅</span>}
                  Пометить как выполненный
                </button>
            }
          </div>

          {/* Детали заказа */}
          <div className="well">
            <p className="text-lg font-bold mb-4">Детали заказа:</p>
            
            <div className="mb-6">
              <div className="font-semibold text-base mb-2">Заказ:</div>
              <div className="whitespace-pre-line bg-gray-100 p-3 rounded border text-left">
                {orderFormatted}
              </div>
            </div>

            <div className="text-left space-y-2">
              <div><span className="font-semibold">Курьер:</span> {order?.courier_full_name || "Не назначен"}</div>
              <div><span className="font-semibold">Получатель:</span> {`${order?.user_full_name} (${order?.user_id})` || "Не назначен"}</div>
              <div><span className="font-semibold">Цена:</span> {order.price} PLN</div>
              <div><span className="font-semibold">Доставка:</span> {order.delivery_price} PLN</div>
              <div><span className="font-semibold">Адрес:</span> {order.address}</div>
              <div><span className="font-semibold">Адрес дополнение:</span> {order.address2}</div>
            </div>
          </div>

          {/* Красивая секция комментария */}
          <div className="well">
            <p className="text-lg font-bold mb-4">Комментарий для клиента:</p>
            
            {editComment ? (
              <div className="bg-gray-50 border-2 border-gray-200 rounded-lg p-4 mb-3">
                <textarea
                  value={commentValue}
                  onChange={(e) => setCommentValue(e.target.value)}
                  className="w-full min-h-20 p-3 border border-gray-300 rounded text-gray-800 resize-y focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Введите комментарий для клиента..."
                />
                <div className="mt-3 flex gap-3 justify-end">
                  <button 
                    onClick={saveComment}
                    className="bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-4 rounded transition-colors"
                  >
                    💾 Сохранить
                  </button>
                  <button 
                    onClick={cancelEditComment}
                    className="bg-gray-500 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded transition-colors"
                  >
                    ❌ Отмена
                  </button>
                </div>
              </div>
            ) : (
              <div className={`${order.comment ? 'bg-green-50 border-green-200 border-2' : 'bg-red-50 border-red-200 border-2'} rounded-lg p-4 relative mb-3`}>
                <div className={`text-base text-left leading-relaxed min-h-6 ${order.comment ? 'text-green-800' : 'text-red-800 italic'}`}>
                  {order.comment || 'Комментарий не добавлен'}
                </div>
                
                <button 
                  onClick={startEditComment}
                  className="absolute top-3 right-3 bg-blue-500 hover:bg-blue-600 text-white font-bold py-1 px-3 rounded text-sm transition-colors"
                >
                  ✏️ Редактировать
                </button>
              </div>
            )}
          </div>

          {/* Фото */}
          <div className="well">
            <p className="text-lg font-bold mb-4">Фото:</p>
            {editPhotos ? 
              <div>
                <div className="mb-3 space-y-2">
                  <input 
                    type="file" 
                    onChange={(e) => onPhotoAdded(0, e)} 
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                  <input 
                    type="file" 
                    onChange={(e) => onPhotoAdded(1, e)}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                </div>
                <div className="flex gap-3">
                  <button 
                    onClick={cancelEditPhotos}
                    className="bg-gray-500 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded transition-colors"
                  >
                    Отмена
                  </button>
                  <button 
                    onClick={uploadPhotos}
                    className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded transition-colors"
                  >
                    Сохранить
                  </button>
                </div>
              </div>
              :
              <div>
                {order.photos ? 
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    {order.photos.split(",").map(photo => 
                      <img 
                        key={photo} 
                        className="w-full rounded-lg shadow-md" 
                        src={`${process.env.REACT_APP_API_URL}/delivery_orders_photos/${photo}`}
                        alt="Фото заказа"
                      />
                    )}
                  </div> :
                  <span className="text-gray-500">Не загружены</span>
                }
                <button 
                  onClick={() => {
                    setEditPhotos(true);
                    setPhotos([])
                  }}
                  className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-2 px-4 rounded transition-colors"
                >
                  Изменить фото
                </button>
              </div>
            }
          </div>

          {/* История */}
          <div className="well">
            <p className="text-lg font-bold mb-4">История:</p>
            <div className="overflow-x-auto">
              <table className="mx-auto border-separate border-spacing-4">
                <tbody className="text-sm">
                <tr>
                  <td className="text-right font-bold pr-4">Создан:</td>
                  <td className="text-left">{formatDate(order?.created_at)}</td>
                </tr>
                <tr>
                  <td className="text-right font-bold pr-4">Подтвержден:</td>
                  <td className="text-left">{formatDate(order?.confirmed_at)}</td>
                </tr>
                <tr>
                  <td className="text-right font-bold pr-4">Выполнен:</td>
                  <td className="text-left">{formatDate(order?.done_at)}</td>
                </tr>
                <tr>
                  <td className="text-right font-bold pr-4">Получен:</td>
                  <td className="text-left">{formatDate(order?.received_at)}</td>
                </tr>
                <tr>
                  <td className="text-right font-bold pr-4">Отменен:</td>
                  <td className="text-left">{formatDate(order?.cancelled_at)}</td>
                </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    }

  </div>
}
